import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { getLanBase, rewriteToLan } from '../lib/lanStorage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Given an in-app download URL (like `/api/shizai/receipts/photo/xxx`),
 * return an optimized URL that hits the NAS directly over LAN when possible.
 *
 * Behaviour:
 *  - If no LAN base cached OR the endpoint is empty → returns the original URL.
 *  - If LAN base is cached → asks the backend for a signed direct URL, then
 *    rewrites the host to the LAN base and returns that. The signed URL is
 *    valid for 5 min so we intentionally do NOT cache it long-term — it
 *    would expire mid-page. Instead we resolve fresh on every mount, which
 *    is a single lightweight round-trip.
 *  - If the backend can't sign (file is on Emergent, not NAS) → falls back
 *    to the original URL silently.
 *
 * The returned URL always begins with a leading '/' or 'http' — safe to
 * pass directly to <img src>.
 */
export function useLanDirectUrl(originalPath) {
  const [url, setUrl] = useState('');
  const requestedRef = useRef(null);

  useEffect(() => {
    if (!originalPath) { setUrl(''); return; }
    const wan = originalPath.startsWith('http')
      ? originalPath
      : `${process.env.REACT_APP_BACKEND_URL}${originalPath}`;
    // If we have no LAN base cached, skip the round trip entirely
    if (!getLanBase()) { setUrl(wan); return; }
    // Avoid duplicate requests for the same path
    if (requestedRef.current === originalPath) return;
    requestedRef.current = originalPath;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(`${API}/storage/direct-url`,
          { endpoint: originalPath },
          { withCredentials: true, timeout: 3000 });
        if (cancelled) return;
        const direct = res?.data?.direct_url;
        if (!direct) { setUrl(wan); return; }
        setUrl(rewriteToLan(direct));
      } catch {
        if (!cancelled) setUrl(wan);
      }
    })();
    return () => { cancelled = true; };
  }, [originalPath]);

  // Return the WAN URL immediately so <img> can start loading; the effect
  // will swap in the LAN URL when ready. Browsers cancel the original
  // request as soon as `src` changes, so no wasted bandwidth.
  const wanFallback = originalPath
    ? (originalPath.startsWith('http') ? originalPath : `${process.env.REACT_APP_BACKEND_URL}${originalPath}`)
    : '';
  return url || wanFallback;
}
